import React, { useState, useMemo } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  AlertTriangle, DollarSign, HardHat, RotateCcw,
  BarChart3, Package, Ban, ShieldCheck, FileText, CheckCircle2, ArrowRight,
} from 'lucide-react';
import {
  useGlosarItemMedicao,
  CategoriaGlosa, EscopoGlosa, AbrangenciaGlosa,
  NATUREZA_OPTIONS,
} from '@/hooks/useEmpGlosarItem';

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

interface ItemContext {
  id: string;
  planilha_item_id: string;
  quantidade_medida: number;
  valor_unitario: number;
  valor_periodo: number;
  item_codigo?: string;
  item_descricao?: string;
}

interface GlosaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Item pre-selected (when opening from item row) */
  item?: ItemContext | null;
  /** Restrict scope — when opened from an item context, lock to 'item' */
  escopoFixo?: EscopoGlosa;
  boletimId?: string;
  contratoId: string;
  /** Optional: navigate to glosas tab */
  onNavigateGlosas?: () => void;
}

// ═══════════════════════════════════════════════════════════════
// Formatters
// ═══════════════════════════════════════════════════════════════
import { fmtCurrency as fmtCur } from '@/lib/formatters';

// ═══════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════
export function GlosaDialog({
  open, onOpenChange, item, escopoFixo, boletimId, contratoId, onNavigateGlosas,
}: GlosaDialogProps) {
  // Step state
  const [escopo, setEscopo] = useState<EscopoGlosa | null>(escopoFixo ?? null);
  const [categoria, setCategoria] = useState<CategoriaGlosa | null>(null);
  const [abrangencia, setAbrangencia] = useState<AbrangenciaGlosa>('total');
  const [justificativa, setJustificativa] = useState('');
  const [qtdAprovada, setQtdAprovada] = useState('');
  const [valorGlosado, setValorGlosado] = useState('');
  const [natureza, setNatureza] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [successResult, setSuccessResult] = useState<{ impacto: string; escopo: EscopoGlosa } | null>(null);
  const mutation = useGlosarItemMedicao();

  // Resolve escopo from context
  const resolvedEscopo = escopoFixo ?? escopo;

  const reset = () => {
    setEscopo(escopoFixo ?? null);
    setCategoria(null);
    setAbrangencia('total');
    setJustificativa('');
    setQtdAprovada('');
    setValorGlosado('');
    setNatureza('');
    setShowConfirm(false);
    setSuccessResult(null);
  };

  // ─── Validations ───
  const justificativaValida = justificativa.trim().length >= 20;

  const qtdAprovadaNum = parseFloat(qtdAprovada);
  const parcialValida = categoria === 'quantitativa' && abrangencia === 'parcial' && item
    ? !isNaN(qtdAprovadaNum) && qtdAprovadaNum > 0 && qtdAprovadaNum < item.quantidade_medida
    : true;

  const valorGlosadoNum = parseFloat(valorGlosado);
  const financeiraValida = categoria === 'financeira'
    ? !isNaN(valorGlosadoNum) && valorGlosadoNum > 0 &&
      (resolvedEscopo === 'contrato' || (item && valorGlosadoNum <= item.valor_periodo))
    : true;

  const canSubmit = useMemo(() => {
    if (!resolvedEscopo || !categoria) return false;
    if (!justificativaValida) return false;
    if (categoria === 'quantitativa' && resolvedEscopo !== 'item') return false;
    if (categoria === 'quantitativa' && abrangencia === 'parcial' && !parcialValida) return false;
    if (categoria === 'financeira' && !financeiraValida) return false;
    if (resolvedEscopo === 'item' && !item) return false;
    if (mutation.isPending) return false;
    return true;
  }, [resolvedEscopo, categoria, justificativaValida, parcialValida, financeiraValida, item, abrangencia, mutation.isPending]);

  // ─── Preview calculations ───
  const qtdGlosada = categoria === 'quantitativa' && item
    ? (abrangencia === 'total' ? item.quantidade_medida : (parcialValida ? item.quantidade_medida - qtdAprovadaNum : 0))
    : 0;

  const valorPreview = categoria === 'financeira'
    ? (financeiraValida ? valorGlosadoNum : 0)
    : qtdGlosada * (item?.valor_unitario ?? 0);

  const valorAntes = item?.valor_periodo ?? 0;
  const valorDepois = categoria === 'financeira' && resolvedEscopo === 'item'
    ? valorAntes - valorPreview
    : categoria === 'quantitativa' && item
      ? (item.quantidade_medida - qtdGlosada) * item.valor_unitario
      : 0;

  // ─── Confirm ───
  const handleConfirm = () => {
    mutation.mutate({
      categoria: categoria!,
      escopo: resolvedEscopo!,
      abrangencia: categoria === 'quantitativa' ? abrangencia : undefined,
      medicaoItemId: resolvedEscopo === 'item' ? item?.id : undefined,
      contratoId,
      boletimId,
      quantidadeAprovada: categoria === 'quantitativa' && abrangencia === 'parcial'
        ? qtdAprovadaNum : undefined,
      valorGlosado: categoria === 'financeira' ? valorGlosadoNum : undefined,
      natureza: natureza.trim() || undefined,
      justificativa: justificativa.trim(),
    }, {
      onSuccess: (result) => {
        setSuccessResult({
          impacto: result.impacto_resumido,
          escopo: resolvedEscopo!,
        });
        setShowConfirm(false);
      },
      onSettled: () => setShowConfirm(false),
    });
  };

  // ─── Confirm message ───
  const confirmMsg = categoria === 'financeira'
    ? resolvedEscopo === 'contrato'
      ? `Será aplicado um ajuste financeiro de ${fmtCur(valorPreview)} diretamente no contrato. Esta ação gera rastreabilidade permanente.`
      : `Ajuste financeiro de ${fmtCur(valorPreview)} no item. O serviço permanece aceito fisicamente. Esta ação gera rastreabilidade permanente.`
    : abrangencia === 'total'
      ? `Toda a quantidade medida (${item?.quantidade_medida.toLocaleString('pt-BR')}) será rejeitada e retornará para correção. Esta ação gera rastreabilidade permanente.`
      : `A quantidade aprovada será ${qtdAprovadaNum?.toLocaleString('pt-BR')} e o restante (${qtdGlosada.toLocaleString('pt-BR')}) retornará para correção. Esta ação gera rastreabilidade permanente.`;

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Nova Glosa
            </DialogTitle>
            <DialogDescription>
              Selecione o tipo de ajuste e preencha os campos obrigatórios.
            </DialogDescription>
          </DialogHeader>

          {/* ── Success state with CTAs ── */}
          {successResult ? (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-primary/5 border border-primary/20">
                <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Glosa registrada com sucesso</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{successResult.impacto}</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Próximas ações:</p>
                {onNavigateGlosas && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-left"
                    onClick={() => { reset(); onOpenChange(false); onNavigateGlosas(); }}
                  >
                    <ArrowRight className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                    Ver todas as glosas deste boletim
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-left"
                  onClick={() => { reset(); /* keep dialog open for another glosa */ }}
                >
                  <ArrowRight className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                  Registrar outra glosa
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-left text-muted-foreground"
                  onClick={() => { reset(); onOpenChange(false); }}
                >
                  Fechar e continuar análise
                </Button>
              </div>
            </div>
          ) : (
          <>

          {/* ── Scope badge ── */}
          <div className="flex items-center gap-2">
            {resolvedEscopo === 'item' && (
              <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                <FileText className="h-3 w-3 mr-1" /> Glosa no Item
              </Badge>
            )}
            {resolvedEscopo === 'contrato' && (
              <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-600">
                <DollarSign className="h-3 w-3 mr-1" /> Glosa no Contrato
              </Badge>
            )}
          </div>

          <div className="space-y-4">
            {/* ── Step 0: Scope selection (only when not fixed) ── */}
            {!escopoFixo && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Escopo <span className="text-destructive">*</span></Label>
                <RadioGroup
                  value={escopo ?? ''}
                  onValueChange={(v) => {
                    setEscopo(v as EscopoGlosa);
                    setCategoria(null);
                  }}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="item" id="escopo-item" />
                    <Label htmlFor="escopo-item" className="cursor-pointer">Item de medição</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="contrato" id="escopo-contrato" />
                    <Label htmlFor="escopo-contrato" className="cursor-pointer">Contrato</Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* ── Item info (when item scope) ── */}
            {resolvedEscopo === 'item' && item && (
              <div className="bg-muted/40 rounded-md p-3 space-y-1">
                <p className="text-sm font-medium">{item.item_codigo} — {item.item_descricao}</p>
                <div className="flex gap-4 text-xs text-muted-foreground">
                  <span>Qtd medida: <strong className="text-foreground">{item.quantidade_medida.toLocaleString('pt-BR')}</strong></span>
                  <span>Valor: <strong className="text-foreground">{fmtCur(item.valor_periodo)}</strong></span>
                </div>
              </div>
            )}

            {/* ── Step 1: Category selection ── */}
            {resolvedEscopo && (
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Tipo de glosa <span className="text-destructive">*</span></Label>
                <div className="grid grid-cols-1 gap-3">
                  {/* Card: Ajustar quantidade (only for item scope) */}
                  {resolvedEscopo === 'item' && (
                    <button
                      type="button"
                      onClick={() => setCategoria('quantitativa')}
                      className={`text-left border-2 rounded-lg p-4 transition-all ${
                        categoria === 'quantitativa'
                          ? 'border-destructive bg-destructive/5 ring-1 ring-destructive/20'
                          : 'border-border hover:border-muted-foreground/30'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`mt-0.5 rounded-full p-1.5 ${categoria === 'quantitativa' ? 'bg-destructive/10 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                          <HardHat className="h-4 w-4" />
                        </div>
                        <div className="flex-1 space-y-1.5">
                          <p className="text-sm font-semibold">Ajustar quantidade</p>
                          <p className="text-xs text-muted-foreground">
                            Altera quantidade e valor. Pode retornar saldo para execução.
                          </p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-2 border-t border-border/50">
                            <span className="flex items-center gap-1"><RotateCcw className="h-3 w-3 text-destructive" /> Retorna para execução</span>
                            <span className="flex items-center gap-1"><BarChart3 className="h-3 w-3 text-destructive" /> Impacta avanço físico</span>
                            <span className="flex items-center gap-1"><Package className="h-3 w-3 text-destructive" /> Retorna saldo</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Card: Ajustar valor */}
                  <button
                    type="button"
                    onClick={() => setCategoria('financeira')}
                    className={`text-left border-2 rounded-lg p-4 transition-all ${
                      categoria === 'financeira'
                        ? 'border-amber-500 bg-amber-500/5 ring-1 ring-amber-500/20'
                        : 'border-border hover:border-muted-foreground/30'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 rounded-full p-1.5 ${categoria === 'financeira' ? 'bg-amber-500/10 text-amber-600' : 'bg-muted text-muted-foreground'}`}>
                        <DollarSign className="h-4 w-4" />
                      </div>
                      <div className="flex-1 space-y-1.5">
                        <p className="text-sm font-semibold">Ajustar valor</p>
                        <p className="text-xs text-muted-foreground">
                          Altera apenas o valor, sem mudar a quantidade.
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground pt-2 border-t border-border/50">
                          <span className="flex items-center gap-1"><DollarSign className="h-3 w-3 text-amber-500" /> Ajusta valor do boletim</span>
                          <span className="flex items-center gap-1"><Ban className="h-3 w-3 text-muted-foreground" /> NÃO altera avanço físico</span>
                          <span className="flex items-center gap-1"><Ban className="h-3 w-3 text-muted-foreground" /> NÃO retorna saldo</span>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* ── Step 2a: Abrangência (quantitativa only) ── */}
            {categoria === 'quantitativa' && (
              <div className="space-y-2">
                <Label>Abrangência</Label>
                <RadioGroup value={abrangencia} onValueChange={(v) => setAbrangencia(v as AbrangenciaGlosa)} className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="total" id="abr-total" />
                    <Label htmlFor="abr-total" className="cursor-pointer">Glosa total</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="parcial" id="abr-parcial" />
                    <Label htmlFor="abr-parcial" className="cursor-pointer">Aprovação parcial</Label>
                  </div>
                </RadioGroup>
              </div>
            )}

            {/* ── Step 2b: Quantidade aprovada (parcial) ── */}
            {categoria === 'quantitativa' && abrangencia === 'parcial' && item && (
              <div className="space-y-2">
                <Label>Quantidade aprovada</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={item.quantidade_medida - 0.01}
                  value={qtdAprovada}
                  onChange={(e) => setQtdAprovada(e.target.value)}
                  placeholder={`Máx: ${(item.quantidade_medida - 0.01).toLocaleString('pt-BR')}`}
                />
                {parcialValida && qtdAprovadaNum > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Quantidade rejeitada: <strong className="text-destructive">{qtdGlosada.toLocaleString('pt-BR')}</strong> → retorna para correção
                  </p>
                )}
                {!parcialValida && qtdAprovada.length > 0 && (
                  <p className="text-xs text-destructive">Quantidade inválida. Deve ser entre 0 e {item.quantidade_medida}.</p>
                )}
              </div>
            )}

            {/* ── Step 2c: Valor (financeira) ── */}
            {categoria === 'financeira' && (
              <div className="space-y-2">
                <Label>Valor do ajuste (R$) <span className="text-destructive">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={resolvedEscopo === 'item' && item ? item.valor_periodo : undefined}
                  value={valorGlosado}
                  onChange={(e) => setValorGlosado(e.target.value)}
                  placeholder={resolvedEscopo === 'item' && item
                    ? `Máx: ${fmtCur(item.valor_periodo)}`
                    : 'Valor do ajuste'}
                />
                {valorGlosadoNum <= 0 && valorGlosado.length > 0 && (
                  <p className="text-xs text-destructive">O valor deve ser positivo.</p>
                )}
                {resolvedEscopo === 'item' && item && valorGlosadoNum > item.valor_periodo && (
                  <p className="text-xs text-destructive">Valor não pode exceder {fmtCur(item.valor_periodo)}.</p>
                )}
              </div>
            )}

            {/* ── Natureza (optional) ── */}
            {categoria && (
              <div className="space-y-2">
                <Label>Natureza <span className="text-xs text-muted-foreground font-normal">(opcional)</span></Label>
                <Select value={natureza} onValueChange={setNatureza}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a natureza..." />
                  </SelectTrigger>
                  <SelectContent>
                    {NATUREZA_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* ── Justificativa ── */}
            {categoria && (
              <div className="space-y-2">
                <Label>Justificativa <span className="text-destructive">*</span></Label>
                <Textarea
                  value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  placeholder="Descreva o motivo da glosa (mínimo 20 caracteres)..."
                  rows={3}
                />
                <div className="flex items-center gap-2 text-xs">
                  <span className={justificativaValida ? 'text-muted-foreground' : justificativa.length > 0 ? 'text-destructive' : 'text-muted-foreground'}>
                    {justificativa.trim().length}/20 caracteres mínimos
                  </span>
                  {!justificativaValida && justificativa.length > 0 && (
                    <span className="text-destructive">— insuficiente</span>
                  )}
                </div>
              </div>
            )}

            {/* ── Impact Preview ── */}
            {categoria && canSubmit && (
              <div className={`border rounded-md p-3 space-y-2 ${
                categoria === 'financeira'
                  ? 'bg-amber-500/5 border-amber-500/20'
                  : 'bg-destructive/5 border-destructive/20'
              }`}>
                <p className={`text-sm font-medium ${
                  categoria === 'financeira' ? 'text-amber-700 dark:text-amber-400' : 'text-destructive'
                }`}>
                  Impacto da glosa
                </p>
                <div className="text-xs space-y-1">
                  {/* Item-level impact */}
                  {resolvedEscopo === 'item' && item && (
                    <>
                      {categoria === 'financeira' ? (
                        <div className="space-y-0.5">
                          <p className="font-medium">
                            {fmtCur(valorAntes)} → {fmtCur(valorDepois)}{' '}
                            <span className="text-destructive font-bold">({fmtCur(-valorPreview)})</span>
                          </p>
                          <p className="text-muted-foreground">Neste item — quantidade física permanece inalterada.</p>
                        </div>
                      ) : (
                        <div className="space-y-0.5">
                          <p>Quantidade rejeitada: <strong className="text-destructive">{qtdGlosada.toLocaleString('pt-BR')}</strong></p>
                          <p className="font-medium">
                            {fmtCur(valorAntes)} → {fmtCur(valorDepois)}{' '}
                            <span className="text-destructive font-bold">({fmtCur(-valorPreview)})</span>
                          </p>
                          <p className="text-muted-foreground">O serviço retornará para reprocessamento.</p>
                        </div>
                      )}
                    </>
                  )}

                  {/* Contract-level impact */}
                  {resolvedEscopo === 'contrato' && (
                    <div className="space-y-0.5">
                      <p className="font-medium">
                        Impacto no contrato: <span className="text-destructive font-bold">{fmtCur(-valorPreview)}</span>
                      </p>
                      <p className="text-muted-foreground">Ajuste direto no faturamento — sem vínculo a item.</p>
                    </div>
                  )}

                  {natureza && (
                    <p className="text-muted-foreground pt-1">
                      Natureza: {NATUREZA_OPTIONS.find(o => o.value === natureza)?.label ?? natureza}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} disabled={mutation.isPending}>
              Cancelar
            </Button>
            <Button
              variant={categoria === 'financeira' ? 'default' : 'destructive'}
              onClick={() => setShowConfirm(true)}
              disabled={!canSubmit}
            >
              Registrar Glosa
            </Button>
          </DialogFooter>
          </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Confirmar glosa
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmMsg}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} disabled={mutation.isPending}>
              {mutation.isPending ? 'Aplicando...' : 'Confirmar e registrar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
